import { Injectable } from "@angular/core";
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { Router } from "@angular/router";
import { catchError } from "rxjs/operators";
import * as Sentry from "@sentry/angular";
import { OwnerService } from "app/main/owner/owner.service";
import { BuildingService } from "app/main/building/building.service";
import { SecurityService } from "app/main/security/security.service";
import { getDeviceId } from "app/shared/device-id";
import { fuseConfig } from "app/fuse-config";

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private ownerService: OwnerService,
        private buildingService: BuildingService,
        private securityService: SecurityService
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const token = localStorage.getItem("token");

        const headers: { [name: string]: string } = {};

        // Auth/device headers only belong on our own APIs. Third-party hosts
        // (e.g. captcha.goutru.com) reject unknown headers in their CORS
        // preflight, which kills the request before it leaves the browser.
        const isOwnApi =
            request.url.startsWith(fuseConfig.url) ||
            request.url.startsWith(fuseConfig.url2) ||
            !/^https?:\/\//i.test(request.url);

        if (isOwnApi) {
            if (token) {
                headers['Authorization'] = token;
            }

            // Stable per-browser device id (additive; absent => per-user fallback on API)
            if (!request.headers.has('X-Device-Id')) {
                const deviceId = getDeviceId();
                if (deviceId) {
                    headers['X-Device-Id'] = deviceId;
                }
            }
        }

        if (!request.headers.has('Content-Type')) {
            headers['Content-Type'] = 'application/json';
        }

        const clonedRequest = request.clone({
            setHeaders: headers,
            withCredentials: true,
        });
        
        return next.handle(clonedRequest).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 403) {
                    const code = err.error?.message?.code;
                    if (code === "S0001" || code === "S9999") {
                        localStorage.removeItem("token");
                        sessionStorage.removeItem("token");
                        this.buildingService.building = null;
                        this.ownerService.unit = null;
                        this.securityService.gate = null;
                        Sentry.setUser(null);
                        this.router.navigate(["/auth/login"]);
                        return throwError(err);
                    } else if (code === "S0002") {
                        this.router.navigate(["/profile"]);
                        return throwError(err);
                    }
                }
                return throwError(err);
            })
        );
    }
}